import { useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  HandHeart, Apple, Users, HeartHandshake, MapPin, Check, ChevronRight, ChevronLeft,
  Loader2, Copy, CheckCircle2, Search, ArrowRight, RotateCcw, AlertCircle,
} from 'lucide-react'
import { PortalLayout } from '../../components/portal/PortalLayout'
import { Seo } from '../../components/portal/Seo'
import { Container, Section, SectionHeading, Breadcrumbs, Card, Button, Alert, FormField, Badge } from '../../components/portal/ui'
import {
  registerSupporter,
  trackSupporter,
  REGISTRATION_TYPE_LABELS,
  SUPPORT_TYPE_LABELS,
  SUPPORTER_STATUS_LABELS,
  WIZARD_SUPPORT_TYPES,
  PATIENT_SUPPORT_OPTIONS,
  type RegistrationType,
  type SupportType,
  type PatientSupportOption,
  type SupporterRegistration,
  type SupporterTrackResponse,
  type RegistrationStatus,
} from '../../services/supporterService'
import { listLocations, type FacilityLocations } from '../../services/facilityService'

const STEP_ICONS = {
  NUTRITION: Apple,
  FAMILY_SUPPORT: Users,
  PSYCHOSOCIAL_SUPPORT: HeartHandshake,
  OTHER_APPROVED: HandHeart,
} as const

const STATUS_TONE: Record<RegistrationStatus, 'blue' | 'amber' | 'green' | 'slate' | 'teal'> = {
  NEW: 'blue',
  UNDER_REVIEW: 'amber',
  APPROVED: 'green',
  REJECTED: 'slate',
  ONBOARDED: 'teal',
}

const ALL_REG_TYPES = Object.keys(REGISTRATION_TYPE_LABELS) as RegistrationType[]
const ORG_TYPES: RegistrationType[] = ['NIKSHAY_MITRA_NGO', 'NIKSHAY_MITRA_CORPORATE']

export default function BecomeASupporterPage() {
  const { t, i18n } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()

  // ---- wizard state ----
  const [step, setStep] = useState(1)
  const [data, setData] = useState({
    registrationType: 'NIKSHAY_MITRA_INDIVIDUAL' as RegistrationType,
    supportType: '' as SupportType | '',
    supportTypeDetail: '',
    state: '',
    district: '',
    taluka: '',
    city: '',
    patientSupportOption: '' as PatientSupportOption | '',
    name: '',
    email: '',
    phone: '',
    organizationName: '',
    commitmentLevel: 'one_time',
    numberOfPatients: '',
    message: '',
  })
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [result, setResult] = useState<SupporterRegistration | null>(null)

  // ---- location tree ----
  const [locations, setLocations] = useState<FacilityLocations | null>(null)
  const [locError, setLocError] = useState<string | null>(null)

  // ---- track state ----
  const [trackUuid, setTrackUuid] = useState('')
  const [trackResult, setTrackResult] = useState<SupporterTrackResponse | null>(null)
  const [trackError, setTrackError] = useState<string | null>(null)
  const [trackLoading, setTrackLoading] = useState(false)
  const [copied, setCopied] = useState(false)

  const stepRef = useRef<HTMLDivElement>(null)

  // Load the verified-facility location tree once (for step 3 dropdowns).
  useEffect(() => {
    listLocations()
      .then(setLocations)
      .catch(() => setLocError(t('pages.becomeSupporter.step3.locationLabel')))
  }, [i18n.language, t])

  // Deep-links from the /support-a-patient info hub:
  //   ?track=1            → jump straight to the "Track my support" step
  //   ?type=<RegType>     → pre-select "registering as" and advance to "how to help"
  useEffect(() => {
    if (searchParams.get('track') === '1') {
      setStep(5)
      searchParams.delete('track')
      setSearchParams(searchParams, { replace: true })
      return
    }
    const type = searchParams.get('type') as RegistrationType | null
    if (type && ALL_REG_TYPES.includes(type)) {
      setData((d) => ({ ...d, registrationType: type }))
      setStep(2)
      searchParams.delete('type')
      setSearchParams(searchParams, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Focus the step panel on every step change for screen-reader + keyboard users.
  useEffect(() => {
    stepRef.current?.focus()
  }, [step])

  const states = useMemo(() => (locations ? Object.keys(locations) : []), [locations])
  const districts = useMemo(
    () => (locations && data.state ? Object.keys(locations[data.state] || {}) : []),
    [locations, data.state],
  )
  const talukas = useMemo(
    () => (locations && data.state && data.district ? locations[data.state][data.district] || [] : []),
    [locations, data.state, data.district],
  )

  // Auto-select the sole state (the seeded tree is Maharashtra-only) so its
  // districts populate without forcing the user to pick a hidden state field.
  useEffect(() => {
    if (states.length === 1 && !data.state) {
      setData((d) => ({ ...d, state: states[0] }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [states])

  const isOrg = ORG_TYPES.includes(data.registrationType)
  const set = (patch: Partial<typeof data>) => setData((d) => ({ ...d, ...patch }))

  const typeLabel = (rt: RegistrationType) => t(`supporterForm.types.${rt}`, { defaultValue: REGISTRATION_TYPE_LABELS[rt] })
  const supportLabel = (st: SupportType) => t(`supporterForm.supportTypes.${st}`, { defaultValue: SUPPORT_TYPE_LABELS[st] })

  const stepper = [
    t('pages.becomeSupporter.stepper.step1'),
    t('pages.becomeSupporter.stepper.step2'),
    t('pages.becomeSupporter.stepper.step3'),
    t('pages.becomeSupporter.stepper.step4'),
    t('pages.becomeSupporter.stepper.step5'),
  ]

  const canContinue = (): boolean => {
    if (step === 1) return !!data.registrationType
    if (step === 2) return !!data.supportType && (data.supportType !== 'OTHER_APPROVED' || data.supportTypeDetail.trim().length > 0)
    if (step === 3) return !!data.patientSupportOption && (!!data.district || !!data.city.trim())
    return true
  }

  const next = () => { if (canContinue() && step < 5) setStep((s) => s + 1) }
  const back = () => { if (step > 1) setStep((s) => s - 1) }
  const goTo = (n: number) => { if (n < step) setStep(n) }

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    const errs: Record<string, string> = {}
    if (!data.name.trim()) errs.name = t('pages.becomeSupporter.step4.errName')
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) errs.email = t('pages.becomeSupporter.step4.errEmail')
    setErrors(errs)
    if (Object.keys(errs).length) return
    setSubmitting(true); setSubmitError(null)
    try {
      const reg = await registerSupporter({
        registrationType: data.registrationType,
        name: data.name.trim(),
        email: data.email.trim(),
        phone: data.phone.trim() || undefined,
        organizationName: isOrg ? data.organizationName.trim() || undefined : undefined,
        state: data.state || undefined,
        district: data.district || undefined,
        taluka: data.taluka || undefined,
        city: data.city.trim() || undefined,
        patientSupportOption: data.patientSupportOption || undefined,
        preferredSupportType: (data.supportType || undefined) as SupportType | undefined,
        supportTypeDetail: data.supportType === 'OTHER_APPROVED' ? data.supportTypeDetail.trim() || undefined : undefined,
        commitmentLevel: data.commitmentLevel || undefined,
        numberOfPatients: data.numberOfPatients ? Number(data.numberOfPatients) : undefined,
        message: data.message.trim() || undefined,
      })
      setResult(reg)
      setStep(5)
    } catch (err: any) {
      setSubmitError(err?.response?.data?.message || t('pages.becomeSupporter.step4.errServer'))
    } finally {
      setSubmitting(false)
    }
  }

  const runTrack = async (uuid: string) => {
    setTrackLoading(true); setTrackError(null); setTrackResult(null)
    try {
      setTrackResult(await trackSupporter(uuid))
    } catch (err: any) {
      setTrackError(err?.response?.data?.message || t('pages.becomeSupporter.step5.lookupError'))
    } finally {
      setTrackLoading(false)
    }
  }

  const copyRef = async () => {
    if (!result?.registrationUuid) return
    try {
      await navigator.clipboard.writeText(result.registrationUuid)
      setCopied(true); setTimeout(() => setCopied(false), 2000)
    } catch { /* clipboard may be blocked; user can select manually */ }
  }

  const startAgain = () => {
    setData({
      registrationType: 'NIKSHAY_MITRA_INDIVIDUAL', supportType: '', supportTypeDetail: '',
      state: '', district: '', taluka: '', city: '', patientSupportOption: '',
      name: '', email: '', phone: '', organizationName: '', commitmentLevel: 'one_time',
      numberOfPatients: '', message: '',
    })
    setResult(null); setTrackResult(null); setTrackUuid(''); setTrackError(null); setErrors({})
    setStep(1)
  }

  const TrackPanel = ({ value }: { value: SupporterTrackResponse }) => (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm text-slate-500">{t('pages.becomeSupporter.step5.statusLabel')}:</span>
        <Badge tone={STATUS_TONE[value.status]}>{SUPPORTER_STATUS_LABELS[value.status]}</Badge>
        <span className="text-xs text-slate-400">{new Date(value.createdAt).toLocaleDateString()}</span>
      </div>
      <div className="text-sm text-slate-700 space-y-1">
        <p><span className="font-medium">{t('pages.becomeSupporter.step5.referenceLabel')}:</span> <code className="bg-slate-100 px-1.5 py-0.5 rounded text-xs">{value.registrationUuid}</code></p>
        <p><span className="font-medium">{t('supporterForm.nameLabel', 'Name')}:</span> {value.name}</p>
        {value.preferredSupportType && <p><span className="font-medium">{t('pages.becomeSupporter.step2.title')}:</span> {supportLabel(value.preferredSupportType)}{value.supportTypeDetail ? ` — ${value.supportTypeDetail}` : ''}</p>}
        {(value.district || value.city) && <p><span className="font-medium"><MapPin className="w-3.5 h-3.5 inline" aria-hidden="true" />:</span> {[value.city, value.taluka, value.district, value.state].filter(Boolean).join(', ')}</p>}
        {value.patientSupportOption && <p><span className="font-medium">{t('pages.becomeSupporter.step3.optionLabel')}:</span> {t(`pages.becomeSupporter.step3.options.${value.patientSupportOption}`, { defaultValue: value.patientSupportOption })}</p>}
      </div>

      {value.adminNotes && (
        <Alert tone="info" title={t('pages.becomeSupporter.step5.adminNotesLabel')}>{value.adminNotes}</Alert>
      )}

      <div>
        <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-2">{t('pages.becomeSupporter.step5.timelineLabel')}</p>
        {value.timeline.length === 0 ? (
          <p className="text-sm text-slate-500">{t('pages.becomeSupporter.step5.noTimeline')}</p>
        ) : (
          <ol className="relative border-l-2 border-slate-200 ml-2 space-y-4">
            {value.timeline.map((e, i) => (
              <li key={i} className="ml-4">
                <span className="absolute -left-[7px] mt-1.5 w-3 h-3 rounded-full bg-[rgb(15_76_129)]" aria-hidden="true" />
                <p className="text-sm font-medium text-slate-800">{SUPPORTER_STATUS_LABELS[e.status]}</p>
                <p className="text-xs text-slate-400">{new Date(e.at).toLocaleString()}</p>
                {e.note && <p className="text-sm text-slate-600 mt-0.5">{e.note}</p>}
              </li>
            ))}
          </ol>
        )}
      </div>
    </div>
  )

  return (
    <PortalLayout>
      <Seo
        title={t('pages.becomeSupporter.seo.title')}
        description={t('pages.becomeSupporter.seo.description')}
        canonicalPath="/become-a-supporter"
        breadcrumbs={[{ name: t('common.home', 'Home'), path: '/' }, { name: t('nav.becomeSupporter', 'Become a Supporter'), path: '/become-a-supporter' }]}
      />

      <Section className="pb-6">
        <Container>
          <Breadcrumbs items={[{ name: t('common.home', 'Home'), path: '/' }, { name: t('nav.becomeSupporter', 'Become a Supporter') }]} />
          <SectionHeading as="h1" eyebrow={t('pages.becomeSupporter.hero.eyebrow')} title={t('pages.becomeSupporter.hero.title')} description={t('pages.becomeSupporter.hero.description')} />
        </Container>
      </Section>

      <Section className="bg-white py-8">
        <Container className="max-w-2xl">
          {/* Stepper */}
          <nav aria-label="progress" className="mb-8">
            <ol className="flex items-center justify-between gap-1">
              {stepper.map((label, i) => {
                const n = i + 1
                const done = n < step
                const active = n === step
                return (
                  <li key={n} className="flex-1 flex items-center gap-1 min-w-0">
                    <button
                      type="button"
                      onClick={() => goTo(n)}
                      disabled={n > step}
                      className="flex items-center gap-2 min-w-0 group disabled:cursor-default"
                      aria-current={active ? 'step' : undefined}
                    >
                      <span
                        className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold border-2 ${
                          active ? 'bg-[rgb(15_76_129)] text-white border-[rgb(15_76_129)]'
                            : done ? 'bg-[rgb(0_168_107)] text-white border-[rgb(0_168_107)]'
                            : 'bg-white text-slate-400 border-slate-200'
                        }`}
                      >
                        {done ? <Check className="w-4 h-4" /> : n}
                      </span>
                      <span className={`text-xs font-medium truncate hidden sm:block ${active ? 'text-slate-900' : done ? 'text-slate-600' : 'text-slate-400'}`}>{label}</span>
                    </button>
                    {i < stepper.length - 1 && <span className={`flex-1 h-0.5 ${done ? 'bg-[rgb(0_168_107)]' : 'bg-slate-200'}`} aria-hidden="true" />}
                  </li>
                )
              })}
            </ol>
          </nav>

          <div ref={stepRef} tabIndex={-1} className="outline-none">
            {/* STEP 1 — intent + who */}
            {step === 1 && (
              <Card className="p-6 sm:p-8">
                <h2 className="text-xl font-bold text-slate-900">{t('pages.becomeSupporter.step1.title')}</h2>
                <p className="text-slate-600 mt-2 text-sm">{t('pages.becomeSupporter.step1.body')}</p>
                <div className="mt-6">
                  <FormField label={t('pages.becomeSupporter.step1.whoLabel')} htmlFor="bs-type">
                    <select id="bs-type" value={data.registrationType} onChange={(e) => set({ registrationType: e.target.value as RegistrationType })} className="select">
                      {ALL_REG_TYPES.map((rt) => <option key={rt} value={rt}>{typeLabel(rt)}</option>)}
                    </select>
                  </FormField>
                </div>
              </Card>
            )}

            {/* STEP 2 — how to help */}
            {step === 2 && (
              <Card className="p-6 sm:p-8">
                <h2 className="text-xl font-bold text-slate-900">{t('pages.becomeSupporter.step2.title')}</h2>
                <p className="text-slate-600 mt-1 text-sm">{t('pages.becomeSupporter.step2.help')}</p>
                <div className="grid sm:grid-cols-2 gap-3 mt-5">
                  {WIZARD_SUPPORT_TYPES.map((st) => {
                    const Icon = STEP_ICONS[st as keyof typeof STEP_ICONS]
                    const selected = data.supportType === st
                    return (
                      <button
                        key={st}
                        type="button"
                        onClick={() => set({ supportType: st })}
                        aria-pressed={selected}
                        className={`text-left p-4 rounded-xl border-2 transition min-h-[44px] flex gap-3 items-start ${selected ? 'border-[rgb(15_76_129)] bg-[rgb(15_76_129)]/5' : 'border-slate-200 hover:border-slate-300'}`}
                      >
                        <span className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${selected ? 'bg-[rgb(15_76_129)] text-white' : 'bg-slate-100 text-slate-600'}`}>
                          <Icon className="w-5 h-5" aria-hidden="true" />
                        </span>
                        <span className="flex-1">
                          <span className="font-semibold text-slate-800 block">{supportLabel(st)}</span>
                        </span>
                        {selected && <Check className="w-5 h-5 text-[rgb(15_76_129)] shrink-0" />}
                      </button>
                    )
                  })}
                </div>
                {data.supportType === 'OTHER_APPROVED' && (
                  <div className="mt-4">
                    <FormField label={t('pages.becomeSupporter.step2.otherDetailLabel')} htmlFor="bs-detail" helpText={t('pages.becomeSupporter.step2.otherDetailHelp')}>
                      <input id="bs-detail" type="text" value={data.supportTypeDetail} onChange={(e) => set({ supportTypeDetail: e.target.value })} className="input" maxLength={300} />
                    </FormField>
                  </div>
                )}
              </Card>
            )}

            {/* STEP 3 — location + patient support option */}
            {step === 3 && (
              <Card className="p-6 sm:p-8">
                <h2 className="text-xl font-bold text-slate-900">{t('pages.becomeSupporter.step3.title')}</h2>
                <p className="text-slate-600 mt-1 text-sm">{t('pages.becomeSupporter.step3.locationLabel')}</p>
                {locError && <Alert tone="warning" title={locError} className="mt-3" />}
                <div className="grid sm:grid-cols-3 gap-4 mt-4">
                  <FormField label={t('pages.becomeSupporter.step3.stateLabel')} htmlFor="bs-state">
                    <select id="bs-state" value={data.state} onChange={(e) => set({ state: e.target.value, district: '', taluka: '' })} className="select">
                      <option value="">—</option>
                      {states.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </FormField>
                  <FormField label={t('pages.becomeSupporter.step3.districtLabel')} htmlFor="bs-district">
                    <select id="bs-district" value={data.district} onChange={(e) => set({ district: e.target.value, taluka: '' })} className="select" disabled={!districts.length}>
                      <option value="">—</option>
                      {districts.map((d) => <option key={d} value={d}>{d}</option>)}
                    </select>
                  </FormField>
                  <FormField label={t('pages.becomeSupporter.step3.talukaLabel')} htmlFor="bs-taluka">
                    <select id="bs-taluka" value={data.taluka} onChange={(e) => set({ taluka: e.target.value })} className="select" disabled={!talukas.length}>
                      <option value="">—</option>
                      {talukas.map((tl) => <option key={tl} value={tl}>{tl}</option>)}
                    </select>
                  </FormField>
                </div>
                <div className="mt-4">
                  <FormField label={t('pages.becomeSupporter.step3.cityLabel')} htmlFor="bs-city">
                    <input id="bs-city" type="text" value={data.city} onChange={(e) => set({ city: e.target.value })} className="input" />
                  </FormField>
                </div>
                <div className="mt-5">
                  <FormField label={t('pages.becomeSupporter.step3.optionLabel')} htmlFor="bs-option">
                    <select id="bs-option" value={data.patientSupportOption} onChange={(e) => set({ patientSupportOption: e.target.value as PatientSupportOption })} className="select">
                      <option value="">—</option>
                      {PATIENT_SUPPORT_OPTIONS.map((o) => <option key={o} value={o}>{t(`pages.becomeSupporter.step3.options.${o}`)}</option>)}
                    </select>
                  </FormField>
                </div>
              </Card>
            )}

            {/* STEP 4 — register (contact details) */}
            {step === 4 && (
              <Card className="p-6 sm:p-8">
                <h2 className="text-xl font-bold text-slate-900">{t('pages.becomeSupporter.step4.title')}</h2>
                <p className="text-slate-600 mt-1 text-sm">{t('pages.becomeSupporter.step4.body')}</p>
                {submitError && <Alert tone="warning" title={submitError} className="mt-4" />}
                <form onSubmit={submit} noValidate className="mt-5 space-y-4">
                  <div className="rounded-lg bg-slate-50 p-3 text-sm text-slate-600 flex flex-wrap gap-x-4 gap-y-1">
                    <span><span className="font-medium">{t('pages.becomeSupporter.step4.typeLabel')}:</span> {typeLabel(data.registrationType)}</span>
                    {data.supportType && <span><span className="font-medium">{t('pages.becomeSupporter.step2.title')}:</span> {supportLabel(data.supportType as SupportType)}{data.supportType === 'OTHER_APPROVED' && data.supportTypeDetail ? ` — ${data.supportTypeDetail}` : ''}</span>}
                    {(data.district || data.city) && <span><span className="font-medium"><MapPin className="w-3.5 h-3.5 inline" aria-hidden="true" />:</span> {[data.city, data.taluka, data.district, data.state].filter(Boolean).join(', ')}</span>}
                    {data.patientSupportOption && <span><span className="font-medium">{t('pages.becomeSupporter.step3.optionLabel')}:</span> {t(`pages.becomeSupporter.step3.options.${data.patientSupportOption}`)}</span>}
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <FormField label={t('pages.becomeSupporter.step4.nameLabel')} htmlFor="bs-name" required error={errors.name}>
                      <input id="bs-name" type="text" required value={data.name} onChange={(e) => set({ name: e.target.value })} className={`input ${errors.name ? 'input-error' : ''}`} autoComplete="name" />
                    </FormField>
                    <FormField label={t('pages.becomeSupporter.step4.emailLabel')} htmlFor="bs-email" required error={errors.email}>
                      <input id="bs-email" type="email" required value={data.email} onChange={(e) => set({ email: e.target.value })} className={`input ${errors.email ? 'input-error' : ''}`} autoComplete="email" />
                    </FormField>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <FormField label={t('pages.becomeSupporter.step4.phoneLabel')} htmlFor="bs-phone">
                      <input id="bs-phone" type="tel" value={data.phone} onChange={(e) => set({ phone: e.target.value })} className="input" autoComplete="tel" />
                    </FormField>
                    {isOrg && (
                      <FormField label={t('pages.becomeSupporter.step4.orgLabel')} htmlFor="bs-org">
                        <input id="bs-org" type="text" value={data.organizationName} onChange={(e) => set({ organizationName: e.target.value })} className="input" />
                      </FormField>
                    )}
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <FormField label={t('pages.becomeSupporter.step4.commitmentLabel')} htmlFor="bs-commit">
                      <select id="bs-commit" value={data.commitmentLevel} onChange={(e) => set({ commitmentLevel: e.target.value })} className="select">
                        {(['one_time', 'monthly', 'long_term'] as const).map((c) => <option key={c} value={c}>{t(`pages.becomeSupporter.step4.commitments.${c}`)}</option>)}
                      </select>
                    </FormField>
                    <FormField label={t('pages.becomeSupporter.step4.numLabel')} htmlFor="bs-num">
                      <input id="bs-num" type="number" min={0} value={data.numberOfPatients} onChange={(e) => set({ numberOfPatients: e.target.value })} className="input" />
                    </FormField>
                  </div>

                  <FormField label={t('pages.becomeSupporter.step4.messageLabel')} htmlFor="bs-msg">
                    <textarea id="bs-msg" rows={3} value={data.message} onChange={(e) => set({ message: e.target.value })} className="input" />
                  </FormField>

                  <p className="text-xs text-slate-500">{t('pages.becomeSupporter.step4.privacyNote')}</p>

                  <div className="flex items-center gap-3">
                    <Button type="submit" variant="primary" disabled={submitting} icon={submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} className="min-h-[44px]">
                      {submitting ? t('pages.becomeSupporter.step4.submitting') : t('pages.becomeSupporter.step4.submit')}
                    </Button>
                    <Button type="button" onClick={back} variant="ghost" icon={<ChevronLeft className="w-4 h-4" />}>{t('pages.becomeSupporter.step4.back')}</Button>
                  </div>
                </form>
              </Card>
            )}

            {/* STEP 5 — track */}
            {step === 5 && (
              <div className="space-y-5">
                {result && (
                  <Card className="p-6 sm:p-8 ring-2 ring-[rgb(0_168_107)]/40">
                    <div className="flex items-center gap-3 mb-3">
                      <CheckCircle2 className="w-8 h-8 text-[rgb(0_168_107)]" aria-hidden="true" />
                      <div>
                        <h2 className="text-xl font-bold text-slate-900">{t('pages.becomeSupporter.step5.submittedTitle')}</h2>
                        <p className="text-sm text-slate-600">{t('pages.becomeSupporter.step5.submittedBody')}</p>
                      </div>
                    </div>
                    <div className="rounded-lg bg-slate-50 p-3 flex items-center gap-2">
                      <span className="text-sm font-medium text-slate-700 shrink-0">{t('pages.becomeSupporter.step5.referenceLabel')}:</span>
                      <code className="text-xs text-slate-800 break-all flex-1">{result.registrationUuid}</code>
                      <Button onClick={copyRef} variant="outline" size="sm" icon={copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}>{copied ? t('pages.becomeSupporter.step5.copied') : t('pages.becomeSupporter.step5.copy')}</Button>
                    </div>
                    <div className="mt-4">
                      <TrackPanel value={{
                        registrationUuid: result.registrationUuid!,
                        name: result.name,
                        registrationType: result.registrationType,
                        preferredSupportType: result.preferredSupportType,
                        supportTypeDetail: result.supportTypeDetail,
                        city: result.city, state: result.state, district: result.district, taluka: result.taluka,
                        patientSupportOption: result.patientSupportOption,
                        status: result.status, adminNotes: result.adminNotes,
                        createdAt: result.createdAt, reviewedAt: result.reviewedAt,
                        timeline: [{ status: result.status, at: result.createdAt }],
                      }} />
                    </div>
                  </Card>
                )}

                <Card className="p-6 sm:p-8">
                  <h2 className="text-lg font-bold text-slate-900">{t('pages.becomeSupporter.step5.lookupTitle')}</h2>
                  <p className="text-sm text-slate-600 mt-1">{t('pages.becomeSupporter.step5.lookupHelp')}</p>
                  {trackError && <Alert tone="warning" title={trackError} className="mt-3" />}
                  <form
                    onSubmit={(e) => { e.preventDefault(); if (trackUuid.trim()) runTrack(trackUuid.trim()) }}
                    className="mt-4 flex flex-wrap items-end gap-3"
                  >
                    <div className="flex-1 min-w-[200px]">
                      <label htmlFor="bs-track" className="sr-only">{t('pages.becomeSupporter.step5.lookupPlaceholder')}</label>
                      <input id="bs-track" type="text" value={trackUuid} onChange={(e) => setTrackUuid(e.target.value)} placeholder={t('pages.becomeSupporter.step5.lookupPlaceholder')} className="input" />
                    </div>
                    <Button type="submit" variant="primary" disabled={trackLoading || !trackUuid.trim()} icon={trackLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} className="min-h-[44px]">{t('pages.becomeSupporter.step5.lookupBtn')}</Button>
                  </form>
                  {trackResult && (
                    <div className="mt-5 border-t border-slate-100 pt-5">
                      <TrackPanel value={trackResult} />
                    </div>
                  )}
                </Card>

                <div className="flex flex-wrap items-center gap-3">
                  <Button onClick={startAgain} variant="ghost" icon={<RotateCcw className="w-4 h-4" />}>{t('pages.becomeSupporter.step5.startAgain')}</Button>
                  <Button to="/support-a-patient" variant="ghost" icon={<ArrowRight className="w-4 h-4" />}>{t('pages.becomeSupporter.step5.learnMore')}</Button>
                </div>
              </div>
            )}

            {/* Footer nav (steps 1-3) */}
            {step < 4 && (
              <div className="flex items-center justify-between mt-6">
                <Button onClick={back} variant="ghost" disabled={step === 1} icon={<ChevronLeft className="w-4 h-4" />}>{t('pages.becomeSupporter.step2.back')}</Button>
                <span className="text-xs text-slate-400">{t('pages.becomeSupporter.step')} {step} {t('pages.becomeSupporter.of')} 5</span>
                <Button onClick={next} variant="primary" disabled={!canContinue()} icon={<ChevronRight className="w-4 h-4" />} className="min-h-[44px]">{t('pages.becomeSupporter.step1.continue')}</Button>
              </div>
            )}
          </div>
        </Container>
      </Section>
    </PortalLayout>
  )
}